// src/components/leads/QuickLeadCapture.tsx
// Streamlined single-page lead capture form for mobile field staff
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Baby,
  Calendar,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Loader2,
  Check,
  X,
  Sparkles,
  Navigation2,
  Plus,
  Trash2,
  Map as MapIcon,
  Pencil,
  Clock,
  PhoneCall,
  CalendarCheck,
  FileText,
  Camera,
  Upload,
  Image,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createLead, createLeadWithAppointment, updateLead } from '../../services/lead.service';
import { projectService } from '../../services/project.service';
import { scheduleService, hourlyAvailabilityToTimeSlots, getAvailableSlotsForDate } from '../../services/schedule.service';
import { emailService } from '../../services/email.service';
import { appointmentService } from '../../services/appointment.service';
import { useProjectSegments } from '../../hooks/useProjectSegments';
import { useProject } from '../../contexts/ProjectContext';
import { SchoolSegmentSelect } from './SchoolSegmentSelect';
import { SignatureCanvas } from '../contracts/SignatureCanvas';
import { seekFindService } from '../../services/seekFind.service';
import { storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Project } from '../../types/project.types';
import { Schedule, TimeSlot } from '../../types/schedule.types';
import toast from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';
import { format, addDays } from 'date-fns';
import { formatTimeRange } from '../../utils/dateUtils';

// Shared library set so this reuses the same Google Maps script the rest of the
// app loads (no duplicate load), giving us Places Autocomplete.
const GMAPS_LIBRARIES: Libraries = ['places', 'drawing', 'geometry'];

type AddressInputMode = 'gps' | 'map' | 'manual';

interface Child {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  grade: string;
  schoolSegmentId?: string; // Per-student campus override (falls back to the lead's)
}

const GRADE_OPTIONS = [
  'Preschool', 'Pre-K', 'Kindergarten', '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th', '9th', '10th', '11th', '12th'
];

const QuickLeadCapture: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  // App-level project cache (loaded once at login). Used as a fallback so the
  // per-project form gates below don't depend on a fresh network read every
  // time the form opens — on weak signal that read can take seconds or fail.
  const { projects: ctxProjects } = useProject();

  // Pre-fill from URL params (from canvassing)
  const prefillProjectId = searchParams.get('projectId') || '';
  const prefillStreet = searchParams.get('street') || '';
  const prefillUnit = searchParams.get('unit') || '';
  const prefillCity = searchParams.get('city') || '';
  const prefillState = searchParams.get('state') || '';
  const prefillZip = searchParams.get('zipCode') || '';
  const prefillName = searchParams.get('parentName') || '';
  const prefillPhone = searchParams.get('phone') || '';
  const prefillEmail = searchParams.get('email') || '';
  const prefillLat = searchParams.get('lat');
  const prefillLng = searchParams.get('lng');
  // When coming from the canvass flow, status/determination can be pre-set
  // so the lead doesn't land as uncontacted/pending when captured in person.
  const prefillStatus = searchParams.get('status') || 'uncontacted';
  const prefillDetermination = searchParams.get('determination') || 'pending';
  // Seek & Find handoff: sf=1 means "write the Success pin on SUBMIT" (no
  // orphan pins from abandoned forms); sfLoc points at an existing survey pin
  // to flip to success + link the lead instead of creating a second pin.
  const fromSeekFind = searchParams.get('sf') === '1';
  const seekFindAccuracy = searchParams.get('sfAcc');
  const seekFindLocId = searchParams.get('sfLoc') || '';

  // Form state
  const [projectId, setProjectId] = useState(prefillProjectId);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // Parent info
  const [parentFirstName, setParentFirstName] = useState(prefillName.split(' ')[0] || '');
  const [parentLastName, setParentLastName] = useState(prefillName.split(' ').slice(1).join(' ') || '');
  const [phone, setPhone] = useState(prefillPhone);
  const [email, setEmail] = useState(prefillEmail);
  const [language, setLanguage] = useState('');

  // Address
  const [street, setStreet] = useState(prefillStreet);
  const [unit, setUnit] = useState(prefillUnit);
  const [city, setCity] = useState(prefillCity);
  const [state, setState] = useState(prefillState);
  const [zipCode, setZipCode] = useState(prefillZip);

  // GPS and Address Mode
  const [addressMode, setAddressMode] = useState<AddressInputMode>(
    prefillLat && prefillLng ? 'gps' : 'manual'
  );
  const [gpsLocation, setGpsLocation] = useState<{ lat: number; lng: number } | null>(
    prefillLat && prefillLng ? { lat: parseFloat(prefillLat), lng: parseFloat(prefillLng) } : null
  );
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  // Google Maps loader (shared with the rest of the app) — powers address
  // type-ahead on the street field.
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GMAPS_LIBRARIES,
  });
  const streetInputRef = useRef<HTMLInputElement>(null);
  const streetAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Attach Google Places Autocomplete to the street field once the script is
  // ready. Selecting a suggestion fills street/city/state/ZIP + coordinates.
  useEffect(() => {
    if (!mapsLoaded || !streetInputRef.current || streetAutocompleteRef.current) return;
    if (typeof google === 'undefined' || !google.maps?.places) return;
    const ac = new google.maps.places.Autocomplete(streetInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'geometry', 'formatted_address'],
    });
    streetAutocompleteRef.current = ac;
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      let streetNumber = '', route = '', locality = '', region = '', postal = '';
      for (const comp of place.address_components || []) {
        if (comp.types.includes('street_number')) streetNumber = comp.long_name;
        if (comp.types.includes('route')) route = comp.long_name;
        if (comp.types.includes('locality')) locality = comp.long_name;
        if (comp.types.includes('administrative_area_level_1')) region = comp.short_name;
        if (comp.types.includes('postal_code')) postal = comp.long_name;
      }
      const line1 = `${streetNumber} ${route}`.trim();
      if (line1) setStreet(line1);
      if (locality) setCity(locality);
      if (region) setState(region);
      if (postal) setZipCode(postal);
      if (place.geometry?.location) {
        setGpsLocation({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
      }
      setAddressMode('manual');
    });
  }, [mapsLoaded]);

  // School segment (campus) — only relevant for multi-school projects
  const { segments } = useProjectSegments(projectId);
  const [schoolSegmentId, setSchoolSegmentId] = useState<string | undefined>(undefined);

  // Signature projects: the on-screen parent signature is the validity proof —
  // the capture form shows attestation/consent/signature and the lead is
  // created valid (see project.settings.signatureCapture).
  const selectedProject = projects.find(p => p.id === projectId)
    || ctxProjects.find(p => p.id === projectId);
  const isSignatureProject = !!selectedProject?.settings?.signatureCapture;
  // A project is chosen but its settings resolved from neither source. Never
  // capture against unknown settings — on a signature project the form would
  // silently render WITHOUT the signature / no-child / business blocks and
  // accept a non-signature lead (field report 2026-08-28).
  const projectUnresolved = !!projectId && !selectedProject;

  // "How did you hear about us?" defaults to the canvasser opening the form —
  // at the door, the canvasser IS how the family heard. Applied once so the
  // field stays editable (flyer/neighbor/event) and clearable.
  const howHeardDefaulted = useRef(false);
  useEffect(() => {
    if (howHeardDefaulted.current) return;
    if (isSignatureProject && userProfile?.name) {
      setHowHeard(`Canvasser: ${userProfile.name}`);
      howHeardDefaulted.current = true;
    }
  }, [isSignatureProject, userProfile]);

  // Stamp the collector's device position at signing (accountability trail —
  // separate from the lead's address GPS, which can come from map/autocomplete).
  const handleSignatureSave = (data: string) => {
    setSignatureData(data);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setSignatureLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          ...(pos.coords.accuracy != null ? { accuracy: pos.coords.accuracy } : {}),
        }),
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  };

  // Children
  const [children, setChildren] = useState<Child[]>([
    { firstName: '', lastName: '', dateOfBirth: '', grade: '' }
  ]);

  // Notes
  const [notes, setNotes] = useState('');

  // Scheduling
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduleType, setScheduleType] = useState<'tour' | 'call' | null>(null);
  const [rideProvided, setRideProvided] = useState(false);
  const [attendEvent, setAttendEvent] = useState(false);

  // Interest-signature capture (signature projects, e.g. Arco Iris TTSD).
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signatureLocation, setSignatureLocation] = useState<{ latitude: number; longitude: number; accuracy?: number } | null>(null);
  const [consentToContact, setConsentToContact] = useState(false);
  const [howHeard, setHowHeard] = useState('');
  const [communitySupporter, setCommunitySupporter] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessRole, setBusinessRole] = useState('');
  const [clientSchedules, setClientSchedules] = useState<Schedule[]>([]);
  const [availableSlots, setAvailableSlots] = useState<{ [date: string]: (TimeSlot & { userName?: string; userId?: string })[] }>({});
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; endTime: string; userId: string } | null>(null);
  const [slotCounts, setSlotCounts] = useState<Map<string, number>>(new Map());
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // UI state
  const [showChildren, setShowChildren] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Document uploads
  const [interestFormFile, setInterestFormFile] = useState<File | null>(null);
  const [interestFormPreview, setInterestFormPreview] = useState<string | null>(null);
  const [confirmationFile, setConfirmationFile] = useState<File | null>(null);
  const [confirmationPreview, setConfirmationPreview] = useState<string | null>(null);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const interestFormInputRef = useRef<HTMLInputElement>(null);
  const confirmationInputRef = useRef<HTMLInputElement>(null);

  // Load projects
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentUser) return;
      try {
        const projectList = await projectService.getUserProjects(currentUser.uid);
        // Filter to lead projects
        const leadProjects = projectList.filter(p => p.projectType === 'lead_program');
        setProjects(leadProjects);

        // Auto-select if only one lead project or if prefilled
        if (prefillProjectId) {
          setProjectId(prefillProjectId);
        } else if (leadProjects.length === 1) {
          setProjectId(leadProjects[0].id);
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        toast.error('Could not load project settings — check signal and reload');
      } finally {
        setIsLoadingProjects(false);
      }
    };
    loadProjects();
  }, [currentUser, prefillProjectId]);

  // Auto-get GPS on mount if mode is GPS and not prefilled
  useEffect(() => {
    if (addressMode === 'gps' && !gpsLocation && navigator.geolocation) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setIsGettingLocation(false);
        },
        () => {
          setIsGettingLocation(false);
          // Fall back to manual mode if GPS fails
          setAddressMode('manual');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [addressMode]);

  // Dates available for scheduling. Extended from 14 → 56 days (8 weeks) so field
  // staff can scroll into future ("coming") weeks — the client sets date-specific
  // availability weeks out (e.g. Vista has slots into late July) and the old
  // 14-day cap hid all of it. The date grid below is vertically scrollable.
  const availableDates = Array.from({ length: 56 }, (_, i) => addDays(new Date(), i));

  // Load schedules when project changes and scheduling is shown
  const loadSchedules = useCallback(async () => {
    if (!projectId) return;

    setLoadingSchedules(true);
    try {
      // Get all schedules for this project directly
      const projectSchedules = await scheduleService.getProjectSchedules(projectId);

      // Filter for active schedules (typically client_user schedules)
      const activeSchedules = projectSchedules.filter(s => s.isActive);
      // Scope to the selected campus: schedules tagged with this segment, plus
      // any untagged (project-wide) schedules. No segment selected → show all.
      const validSchedules = schoolSegmentId
        ? activeSchedules.filter(s => !s.schoolSegmentId || s.schoolSegmentId === schoolSegmentId)
        : activeSchedules;
      setClientSchedules(validSchedules);

      // Process available slots by date
      const slotsByDate: { [date: string]: (TimeSlot & { userName?: string; userId?: string })[] } = {};

      availableDates.forEach(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayOfWeek = date.getDay();
        const daySlots: (TimeSlot & { userName?: string; userId?: string })[] = [];

        validSchedules.forEach(schedule => {
          // Centralized 30-min slot generator. Handles all formats and legacy
          // whole-hour expansion.
          const slots = getAvailableSlotsForDate(schedule, date);
          const capacity = schedule.defaultSlotCapacity ?? 1;
          slots.forEach(({ startTime, endTime }) => {
            daySlots.push({
              dayOfWeek,
              startTime,
              endTime,
              capacity,
              isRecurring: false,
              userName: schedule.userName,
              userId: schedule.userId
            });
          });
        });

        daySlots.sort((a, b) => a.startTime.localeCompare(b.startTime));

        if (daySlots.length > 0) {
          slotsByDate[dateStr] = daySlots;
        }
      });

      setAvailableSlots(slotsByDate);

      // Tally appointments per slot so the UI can hide slots only when they
      // hit the schedule's configured capacity, not after the first booking.
      const appointments = await appointmentService.getAppointmentsByProject(projectId);
      const counts = new Map<string, number>();
      appointments.forEach(apt => {
        // Cancelled/no-show appointments freed their slot lock — don't count them.
        if (apt.status === 'cancelled' || apt.status === 'no-show') return;
        // Count PER campus so a slot full at one campus doesn't block another.
        const campus = (apt as { schoolSegmentId?: string }).schoolSegmentId || 'all';
        const key = `${apt.date}-${apt.startTime}-${apt.endTime}-${apt.userId}-${campus}`;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      setSlotCounts(counts);
    } catch (error) {
      console.error('Error loading schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  }, [projectId, schoolSegmentId]);

  useEffect(() => {
    if (showScheduling && projectId) {
      loadSchedules();
    }
  }, [showScheduling, projectId, loadSchedules]);

  // Clear the chosen campus if it no longer exists on the selected project.
  useEffect(() => {
    if (schoolSegmentId && !segments.some(s => s.id === schoolSegmentId)) {
      setSchoolSegmentId(undefined);
    }
  }, [segments, schoolSegmentId]);

  // Get current location for GPS mode
  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setIsGettingLocation(false);
          // Reverse geocode to get address
          reverseGeocode(position.coords.latitude, position.coords.longitude);
        },
        () => {
          setIsGettingLocation(false);
          toast.error('Unable to get location. Please enter address manually.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      // Try Google Maps first if available
      if (typeof google !== 'undefined' && google.maps) {
        const geocoder = new google.maps.Geocoder();
        const response = await geocoder.geocode({ location: { lat, lng } });

        if (response.results && response.results[0]) {
          const result = response.results[0];
          const components = result.address_components;

          let streetNumber = '';
          let streetName = '';
          let cityName = '';
          let stateName = '';
          let zipCodeValue = '';

          components.forEach(component => {
            if (component.types.includes('street_number')) {
              streetNumber = component.long_name;
            }
            if (component.types.includes('route')) {
              streetName = component.long_name;
            }
            if (component.types.includes('locality')) {
              cityName = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              stateName = component.short_name;
            }
            if (component.types.includes('postal_code')) {
              zipCodeValue = component.long_name;
            }
          });

          if (streetNumber || streetName) {
            setStreet(`${streetNumber} ${streetName}`.trim());
          }
          if (cityName) setCity(cityName);
          if (stateName) setState(stateName);
          if (zipCodeValue) setZipCode(zipCodeValue);
          return;
        }
      }

      // Fallback to OpenStreetMap Nominatim API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();

      if (data && data.address) {
        const addr = data.address;
        const streetNum = addr.house_number || '';
        const streetName = addr.road || addr.street || '';
        if (streetNum || streetName) {
          setStreet(`${streetNum} ${streetName}`.trim());
        }
        if (addr.city || addr.town || addr.village) {
          setCity(addr.city || addr.town || addr.village);
        }
        if (addr.state) {
          // Try to get state abbreviation
          const stateAbbr = getStateAbbreviation(addr.state);
          setState(stateAbbr || addr.state.slice(0, 2).toUpperCase());
        }
        if (addr.postcode) {
          setZipCode(addr.postcode.slice(0, 5));
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      // GPS location is still valid even if geocoding fails
    }
  };

  // Helper to get US state abbreviation
  const getStateAbbreviation = (stateName: string): string => {
    const states: { [key: string]: string } = {
      'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
      'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
      'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
      'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
      'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
      'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
      'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
      'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
      'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
      'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
      'district of columbia': 'DC'
    };
    return states[stateName.toLowerCase()] || '';
  };

  // Format phone number
  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  // Add child
  const addChild = () => {
    setChildren([...children, { firstName: '', lastName: '', dateOfBirth: '', grade: '' }]);
  };

  // Remove child
  const removeChild = (index: number) => {
    if (children.length > 1) {
      setChildren(children.filter((_, i) => i !== index));
    }
  };

  // Update child
  const updateChild = (index: number, field: keyof Child, value: string) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);
  };

  // Handle document file selection
  const handleInterestFormSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setInterestFormFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setInterestFormPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setInterestFormPreview(null);
    }
  };

  const handleConfirmationSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setConfirmationFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConfirmationPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setConfirmationPreview(null);
    }
  };

  const removeInterestForm = () => {
    setInterestFormFile(null);
    setInterestFormPreview(null);
    if (interestFormInputRef.current) {
      interestFormInputRef.current.value = '';
    }
  };

  const removeConfirmation = () => {
    setConfirmationFile(null);
    setConfirmationPreview(null);
    if (confirmationInputRef.current) {
      confirmationInputRef.current.value = '';
    }
  };

  // Upload documents to Firebase Storage
  const uploadDocuments = async (leadId: string): Promise<{ interestForm?: string; confirmationText?: string }> => {
    const documents: { interestForm?: string; confirmationText?: string } = {};

    if (interestFormFile) {
      const timestamp = Date.now();
      const fileName = `${timestamp}_interest_${interestFormFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `leads/${leadId}/${fileName}`);
      await uploadBytes(storageRef, interestFormFile);
      documents.interestForm = await getDownloadURL(storageRef);
    }

    if (confirmationFile) {
      const timestamp = Date.now();
      const fileName = `${timestamp}_confirmation_${confirmationFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `leads/${leadId}/${fileName}`);
      await uploadBytes(storageRef, confirmationFile);
      documents.confirmationText = await getDownloadURL(storageRef);
    }

    return documents;
  };

  // What's still blocking submit — surfaced under the disabled button so field
  // staff can see exactly which required field is missing instead of guessing.
  const missingRequirements = (): string[] => {
    const missing: string[] = [];
    if (!projectId) missing.push('project');
    if (projectUnresolved) missing.push(isLoadingProjects ? 'project settings (still loading — check signal)' : 'project settings (could not load — check signal and reload)');
    if (!parentFirstName.trim() || !parentLastName.trim()) missing.push('parent first + last name');
    if (!isSignatureProject) {
      if (!email.trim() || !email.includes('@')) missing.push('email');
      if (!phone.trim()) missing.push('phone');
    } else if (email.trim() && !email.includes('@')) missing.push('valid email (or leave it blank)');
    if (!(street.trim() && city.trim() && state.trim() && zipCode.trim()) && gpsLocation === null) missing.push('address or GPS location');
    if (!children[0]?.firstName.trim() && !(isSignatureProject && (communitySupporter || isBusiness))) missing.push("first child's name (or check community supporter / business)");
    if (isSignatureProject && isBusiness && !businessName.trim()) missing.push('business name');
    if (isSignatureProject && isBusiness && !businessRole) missing.push('signer role at the business');
    if (isSignatureProject && isBusiness && !(street.trim() && city.trim() && state.trim() && zipCode.trim())) missing.push('full business street address (street, city, state, ZIP)');
    if (segments.length > 0 && !schoolSegmentId) missing.push('school / campus');
    if (isSignatureProject && !signatureData) missing.push(isBusiness ? 'signer signature' : 'parent signature');
    return missing;
  };

  // Validate form
  const isValid = () => {
    // Required: project, parent name (first AND last), email, phone, address OR GPS, at least one child with name
    if (!projectId) return false;
    if (projectUnresolved) return false;
    if (!parentFirstName.trim()) return false;
    if (!parentLastName.trim()) return false; // Last name is now required
    // Signature projects: email/phone are OPTIONAL per the proposal ("email
    // where consent is given, phone where offered") — the signature is the ask.
    if (!isSignatureProject) {
      if (!email.trim() || !email.includes('@')) return false; // Email is now required
      if (!phone.trim()) return false;
    } else if (email.trim() && !email.includes('@')) {
      return false; // if an email was entered, it must at least look like one
    }
    // Address: Either full address fields OR GPS location is required
    const hasFullAddress = street.trim() && city.trim() && state.trim() && zipCode.trim();
    const hasGpsLocation = gpsLocation !== null;
    if (!hasFullAddress && !hasGpsLocation) return false;
    // Community supporters and businesses on signature projects sign without
    // an eligible child.
    if (!children[0]?.firstName.trim() && !(isSignatureProject && (communitySupporter || isBusiness))) return false;
    if (isSignatureProject && isBusiness && !businessName.trim()) return false;
    if (isSignatureProject && isBusiness && !businessRole) return false;
    // Businesses need a real street address on record (GPS-only is not enough).
    if (isSignatureProject && isBusiness && !(street.trim() && city.trim() && state.trim() && zipCode.trim())) return false;
    // Campus is required on multi-school projects so emails/scheduling target
    // the correct school.
    if (segments.length > 0 && !schoolSegmentId) return false;
    // Signature projects: no signature, no lead — the signature IS the product.
    if (isSignatureProject && !signatureData) return false;
    return true;
  };

  // Submit
  const handleSubmit = async () => {
    if (segments.length > 0 && !schoolSegmentId) {
      toast.error('Please select a school / campus for this family.');
      return;
    }
    if (!isValid() || !currentUser) return;

    setIsSubmitting(true);
    try {
      const parentName = `${parentFirstName.trim()} ${parentLastName.trim()}`.trim();
      // userProfile uses a single `name` field — there is no firstName/lastName.
      const createdByUserName = userProfile?.name?.trim() || userProfile?.email || 'Field User';

      const leadData = {
        projectId,
        parentName,
        parentFirstName: parentFirstName.trim(),
        parentLastName: parentLastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        preferredContactMethod: 'phone' as const,
        ...(language ? { language } : {}),
        // Fold the unit into the address string so apartment leads stay
        // discernible in the client CSV (which exports the single address cell),
        // and keep it separately for structured use.
        address: unit.trim() ? `${street.trim()} #${unit.trim()}` : street.trim(),
        ...(unit.trim() ? { unit: unit.trim() } : {}),
        ...(rideProvided ? { rideProvided: true } : {}),
        ...(attendEvent ? { attendEvent: true } : {}),
        ...(isSignatureProject && signatureData ? {
          signature: signatureData,
          signatureCapturedAt: Timestamp.now(),
          ...(signatureLocation ? { signatureLocation } : {}),
          consentToContact,
          ...(howHeard.trim() ? { howHeard: howHeard.trim() } : {}),
          ...(communitySupporter ? { communitySupporter: true } : {}),
          ...(isBusiness ? {
            isBusiness: true,
            businessName: businessName.trim(),
            businessRole,
            businessAddress: `${unit.trim() ? `${street.trim()} #${unit.trim()}` : street.trim()}, ${city.trim()}, ${state.trim()} ${zipCode.trim()}`,
          } : {}),
        } : {}),
        city: city.trim(),
        state: state.trim(),
        zipCode: zipCode.trim(),
        children: children
          .filter(c => c.firstName.trim())
          .map(c => ({
            name: `${c.firstName.trim()} ${c.lastName.trim()}`.trim(),
            firstName: c.firstName.trim(),
            lastName: c.lastName.trim(),
            dateOfBirth: c.dateOfBirth || '',
            grade: c.grade || '',
            status: 'uncontacted' as const,
            ...(c.schoolSegmentId ? { schoolSegmentId: c.schoolSegmentId } : {}),
          })),
        ...(schoolSegmentId
          ? { schoolSegmentId, schoolSegmentName: segments.find(s => s.id === schoolSegmentId)?.name || '' }
          : {}),
        status: prefillStatus as any,
        // Signature projects: the captured signature is the validity proof —
        // the lead is born valid (per-project divergence from the recap rule).
        determination: (isSignatureProject && signatureData ? 'valid' : prefillDetermination) as any,
        quality: 3,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(gpsLocation ? { gpsLocation: { latitude: gpsLocation.lat, longitude: gpsLocation.lng } } : {}),
        createdBy: currentUser.uid,
        capturedBy: currentUser.uid,
        capturedByName: createdByUserName,
        source: createdByUserName,
        createdAt: Timestamp.now(),
        lastUpdated: Timestamp.now(),
      };

      let leadId: string;

      // If appointment is being scheduled, use combined function (single notification)
      // Otherwise, just create lead (lead-only notification)
      if (scheduleType && selectedSlot && selectedDate) {
        // Find client user name from schedules
        const clientSchedule = clientSchedules.find(s => s.userId === selectedSlot.userId);

        const result = await createLeadWithAppointment({
          leadData,
          appointmentData: {
            date: selectedDate,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            eventType: scheduleType === 'tour' ? 'tour' : 'phone',
            clientUserId: selectedSlot.userId,
            ...(clientSchedule?.userName ? { clientUserName: clientSchedule.userName } : {}),
            slotCapacity: clientSchedule?.defaultSlotCapacity ?? 1,
            rideProvided,
          },
          createdByUserId: currentUser.uid,
          createdByUserName,
          projectName: projects.find(p => p.id === projectId)?.name
        });

        leadId = result.leadId;
        toast.success(`Lead captured with ${scheduleType === 'tour' ? 'tour' : 'call'} scheduled!`);
      } else {
        // No appointment - just create lead
        leadId = await createLead(leadData);
        toast.success('Lead captured successfully!');

        // No appointment → send lead-captured email now (onAppointmentCreated
        // won't fire, and the daily recap is hours away).
        if (projectId) {
          const project = projects.find(p => p.id === projectId);
          emailService.sendLeadCapturedEmail({
            projectId,
            projectName: project?.name || 'Project',
            leadName: parentName,
            phone: phone,
            email: email,
            address: `${unit.trim() ? `${street} #${unit.trim()}` : street}, ${city} ${state}`.trim(),
            capturedByName: createdByUserName,
            leadId,
          }).catch(() => {}); // Non-blocking
        }
      }

      // Seek & Find handoff: the Success pin is written NOW, on submit — so
      // every green pin on the universe map is a real captured lead.
      if (leadId) {
        if (seekFindLocId) {
          seekFindService.markSuccess(seekFindLocId, leadId).catch((e) => console.error('Failed to link seek-find pin:', e));
        } else if (fromSeekFind && prefillLat && prefillLng) {
          seekFindService.create({
            projectId,
            location: { lat: parseFloat(prefillLat), lng: parseFloat(prefillLng) },
            outcome: 'success',
            ...(seekFindAccuracy ? { accuracy: parseFloat(seekFindAccuracy) } : {}),
            capturedBy: currentUser.uid,
            capturedByName: createdByUserName,
            leadId,
          } as any).catch((e) => console.error('Failed to log seek-find success pin:', e));
        }
      }

      // Upload documents if any were selected
      if (interestFormFile || confirmationFile) {
        try {
          setUploadingDocs(true);
          const uploadedDocs = await uploadDocuments(leadId);

          // Update lead with document URLs
          if (Object.keys(uploadedDocs).length > 0) {
            await updateLead(leadId, { documents: uploadedDocs });
          }
        } catch (error) {
          console.error('Error uploading documents:', error);
          toast.error('Documents failed to upload');
        } finally {
          setUploadingDocs(false);
        }
      }

      navigate('/leads');
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to capture lead. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 mr-2 hover:bg-white/10 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold flex items-center">
                <Sparkles className="h-5 w-5 mr-2" />
                Quick Lead Capture
              </h1>
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!isValid() || isSubmitting}
            className="px-4 py-2 bg-white text-emerald-600 font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <div className="px-4 py-4 pb-24 space-y-4">
        {/* Project Selection */}
        {/* Project settings not resolved yet (or failed): say so loudly instead
            of quietly rendering the wrong form. Submit is disabled meanwhile. */}
        {projectUnresolved && (
          <div className={`rounded-xl p-3 text-sm font-medium ${isLoadingProjects ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
            {isLoadingProjects
              ? '⏳ Loading project settings… the signature, no-child and business options appear once loaded.'
              : '⚠️ Could not load project settings (weak signal?). Reload the page before capturing — submitting is disabled until settings load.'}
          </div>
        )}

        {projects.length > 1 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Select a project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* School / Campus (multi-school projects only) */}
        {segments.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <SchoolSegmentSelect
              segments={segments}
              value={schoolSegmentId}
              onChange={setSchoolSegmentId}
              label="School / Campus *"
              emptyLabel="Select a school…"
            />
            <p className="text-xs text-gray-500 mt-1">
              Required. Sets the campus for this family — drives the confirmation email address and filters the available tour/call times below.
            </p>
          </div>
        )}

        {/* Parent Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center mb-4">
            <User className="h-5 w-5 text-emerald-600 mr-2" />
            <h2 className="font-semibold text-gray-900">{isBusiness ? 'Signer (business contact)' : 'Parent/Guardian'}</h2>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="First Name *"
                value={parentFirstName}
                onChange={(e) => setParentFirstName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <input
                type="text"
                placeholder="Last Name *"
                value={parentLastName}
                onChange={(e) => setParentLastName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                placeholder="Phone Number *"
                value={phone}
                onChange={handlePhoneChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                placeholder="Email *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                  email && !email.includes('@') ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>

            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
            >
              <option value="">Preferred language…</option>
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-emerald-600 mr-2" />
              <h2 className="font-semibold text-gray-900">{isBusiness ? 'Business address' : 'Address'}</h2>
            </div>
            {gpsLocation && (
              <span className="text-xs text-emerald-600 flex items-center">
                <Navigation2 className="h-3 w-3 mr-1" />
                GPS captured
              </span>
            )}
            {isGettingLocation && (
              <span className="text-xs text-gray-500 flex items-center">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Getting GPS...
              </span>
            )}
          </div>

          {/* Address Input Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => {
                setAddressMode('gps');
                handleGetCurrentLocation();
              }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                addressMode === 'gps'
                  ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
            >
              <Navigation2 className="h-4 w-4" />
              GPS
            </button>
            <button
              type="button"
              onClick={() => setShowMapPicker(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                addressMode === 'map'
                  ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
            >
              <MapIcon className="h-4 w-4" />
              Map
            </button>
            <button
              type="button"
              onClick={() => setAddressMode('manual')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                addressMode === 'manual'
                  ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500'
                  : 'bg-gray-100 text-gray-600 border border-gray-300'
              }`}
            >
              <Pencil className="h-4 w-4" />
              Manual
            </button>
          </div>

          <div className="space-y-3">
            <input
              ref={streetInputRef}
              type="text"
              placeholder="Start typing a street address…"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              autoComplete="off"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />

            <input
              type="text"
              placeholder="Apt / Unit / Suite (optional)"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />

            <div className="grid grid-cols-5 gap-2">
              <input
                type="text"
                placeholder="City *"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="col-span-2 px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <input
                type="text"
                placeholder="State *"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                maxLength={2}
                className="col-span-1 px-3 py-3 border border-gray-300 rounded-lg text-base text-center focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <input
                type="text"
                placeholder="ZIP *"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                maxLength={5}
                className="col-span-2 px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
        </div>

        {/* Children */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowChildren(!showChildren)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center">
              <Baby className="h-5 w-5 text-emerald-600 mr-2" />
              <h2 className="font-semibold text-gray-900">Children</h2>
              <span className="ml-2 text-sm text-gray-500">({children.length})</span>
            </div>
            {showChildren ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showChildren && (
            <div className="px-4 pb-4 space-y-4">
              {children.map((child, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Child {index + 1}</span>
                    {children.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChild(index)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={child.firstName}
                      onChange={(e) => updateChild(index, 'firstName', e.target.value)}
                      className="px-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={child.lastName}
                      onChange={(e) => updateChild(index, 'lastName', e.target.value)}
                      className="px-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">DOB (optional)</label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="date"
                          placeholder="Birth Date"
                          value={child.dateOfBirth}
                          onChange={(e) => updateChild(index, 'dateOfBirth', e.target.value)}
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Grade</label>
                      <div className="relative">
                        <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <select
                          value={child.grade}
                          onChange={(e) => updateChild(index, 'grade', e.target.value)}
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none bg-white"
                        >
                          <option value="">Select…</option>
                          {GRADE_OPTIONS.map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {segments.length > 0 && (
                    <SchoolSegmentSelect
                      segments={segments}
                      value={child.schoolSegmentId}
                      onChange={(id) => updateChild(index, 'schoolSegmentId', id || '')}
                      label="Campus (this child)"
                      emptyLabel="Same as family"
                      compact
                    />
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addChild}
                className="w-full py-2.5 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg flex items-center justify-center hover:border-emerald-500 hover:text-emerald-600 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Another Child
              </button>
            </div>
          )}
        </div>

        {/* Business signer — signature projects only: a business inside the
            boundary signing in support (client ask 2026-08-28). No children;
            the contact person goes in the name fields above and the business
            itself is captured here. Counts toward the same gross threshold. */}
        {isSignatureProject && (
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isBusiness}
                onChange={(e) => {
                  setIsBusiness(e.target.checked);
                  setShowChildren(!e.target.checked && !communitySupporter);
                  if (!e.target.checked) { setBusinessName(''); setBusinessRole(''); }
                }}
                className="h-4 w-4 mt-0.5 text-emerald-600 rounded"
              />
              <span className="text-sm font-medium text-gray-800">
                🏢 This is a business
                <span className="block text-xs font-normal text-gray-500">Business signing in support — put the contact person in the name fields above; no child info needed.</span>
              </span>
            </label>
            {isBusiness && (
              <div className="space-y-2">
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Business name *"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <select
                  value={businessRole}
                  onChange={(e) => setBusinessRole(e.target.value)}
                  className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${businessRole ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <option value="">Signer's role at the business *</option>
                  <option value="Owner">Owner</option>
                  <option value="Manager">Manager</option>
                  <option value="Employee">Employee</option>
                  <option value="Other">Other</option>
                </select>
                <p className="text-xs text-gray-500">Business address: enter the business's full street address in the <span className="font-medium">Business address</span> section above — required (street, city, state, ZIP).</p>
              </div>
            )}
          </div>
        )}

        {/* Community supporter — signature projects only: a qualified signer is
            a household, kids not required. Checking waives the child fields. */}
        {isSignatureProject && (
          <label className="bg-white rounded-xl shadow-sm p-4 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={communitySupporter}
              onChange={(e) => {
                setCommunitySupporter(e.target.checked);
                setShowChildren(!e.target.checked && !isBusiness);
              }}
              className="h-4 w-4 mt-0.5 text-emerald-600 rounded"
            />
            <span className="text-sm font-medium text-gray-800">
              🤝 I have no eligible child but I am still in support
              <span className="block text-xs font-normal text-gray-500">Community members can sign too — child info not needed.</span>
            </span>
          </label>
        )}

        {/* Event RSVP — parent will attend the client's enrollment event.
            Feeds the "Voyage to Vista" column ("RSVP") in the client export.
            Hidden on signature projects (no enrollment event). */}
        {!isSignatureProject && (
        <label className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={attendEvent}
            onChange={(e) => setAttendEvent(e.target.checked)}
            className="h-4 w-4 text-emerald-600 rounded"
          />
          <span className="text-sm font-medium text-gray-800">
            🎟️ Attending Voyage to Vista
            <span className="block text-xs font-normal text-gray-500">Parent RSVP&apos;d to the enrollment event — adds &quot;RSVP&quot; to the client export.</span>
          </span>
        </label>
        )}

        {/* Interest signature + consent — signature projects only. The parent
            signs on-screen; signature + collector GPS + timestamp form the
            accountability record and the lead is created valid. */}
        {isSignatureProject && (
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about us?</label>
              <input
                type="text"
                value={howHeard}
                onChange={(e) => setHowHeard(e.target.value)}
                placeholder="Neighbor, flyer, event…"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentToContact}
                onChange={(e) => setConsentToContact(e.target.checked)}
                className="h-4 w-4 mt-0.5 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-800">
                {selectedProject?.settings?.consentText || 'This family agrees to receive follow-up communications about this program.'}
              </span>
            </label>
            <div>
              <h2 className="font-semibold text-gray-900 mb-1">✍️ Signature of interest</h2>
              <p className="text-xs text-gray-600 mb-2">
                {isBusiness
                  ? (selectedProject?.settings?.businessSignatureAttestation || 'By signing below, I confirm that I am authorized to sign on behalf of this business and that it supports this program.')
                  : (selectedProject?.settings?.signatureAttestation || 'By signing below, I confirm my household\'s interest in this program.')}
              </p>
              <SignatureCanvas
                onSave={handleSignatureSave}
                onClear={() => { setSignatureData(null); setSignatureLocation(null); }}
              />
              {!signatureData && (
                <p className="text-xs text-amber-600 mt-1">A signature is required to submit this lead.</p>
              )}
            </div>
          </div>
        )}

        {/* Schedule Tour or Call — hidden on signature projects (no tours). */}
        {!isSignatureProject && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowScheduling(!showScheduling)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center">
              <CalendarCheck className="h-5 w-5 text-emerald-600 mr-2" />
              <h2 className="font-semibold text-gray-900">Schedule Tour or Call</h2>
              {scheduleType && selectedSlot && (
                <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  {scheduleType === 'tour' ? 'Tour' : 'Call'} scheduled
                </span>
              )}
            </div>
            {showScheduling ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showScheduling && (
            <div className="px-4 pb-4 space-y-4">
              {/* Schedule Type Selection */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setScheduleType(scheduleType === 'tour' ? null : 'tour');
                    setSelectedSlot(null);
                    setSelectedDate('');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                    scheduleType === 'tour'
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-500'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  <Calendar className="h-5 w-5" />
                  Schedule Tour
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setScheduleType(scheduleType === 'call' ? null : 'call');
                    setSelectedSlot(null);
                    setSelectedDate('');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-colors ${
                    scheduleType === 'call'
                      ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  <PhoneCall className="h-5 w-5" />
                  Follow-up Call
                </button>
              </div>

              {/* Ride provided — field staff drives the family to their tour */}
              {scheduleType === 'tour' && (
                <label className="flex items-center gap-3 mt-3 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rideProvided}
                    onChange={(e) => setRideProvided(e.target.checked)}
                    className="h-4 w-4 text-emerald-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-800">
                    🚗 Ride provided
                    <span className="block text-xs font-normal text-gray-500">Field staff will drive this family to the tour.</span>
                  </span>
                </label>
              )}

              {/* Date and Time Selection */}
              {scheduleType && (
                <>
                  {loadingSchedules ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                      <span className="ml-2 text-gray-500">Loading available times...</span>
                    </div>
                  ) : clientSchedules.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Calendar className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No schedules available for this project.</p>
                      <p className="text-sm">Client users need to set up their availability.</p>
                    </div>
                  ) : (
                    <>
                      {/* Date Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Date
                        </label>
                        <div className="grid grid-cols-7 gap-1 max-h-60 overflow-y-auto pr-1">
                          {availableDates.map(date => {
                            const dateStr = format(date, 'yyyy-MM-dd');
                            const hasSlots = !!availableSlots[dateStr];
                            const isSelected = selectedDate === dateStr;

                            return (
                              <button
                                key={dateStr}
                                type="button"
                                onClick={() => hasSlots && setSelectedDate(dateStr)}
                                disabled={!hasSlots}
                                className={`p-2 text-center rounded-lg text-xs ${
                                  isSelected
                                    ? 'bg-emerald-600 text-white'
                                    : hasSlots
                                      ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                      : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium">{format(date, 'EEE')}</div>
                                <div className="text-sm">{format(date, 'd')}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Time Slot Selection */}
                      {selectedDate && availableSlots[selectedDate] && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Available Times
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {availableSlots[selectedDate].map((slot, idx) => {
                              // Match the campus-scoped count key: this lead's campus.
                              const slotKey = `${selectedDate}-${slot.startTime}-${slot.endTime}-${slot.userId}-${schoolSegmentId || 'all'}`;
                              const slotCapacity = slot.capacity ?? 1;
                              const bookedCount = slotCounts.get(slotKey) || 0;
                              const isBooked = bookedCount >= slotCapacity;
                              const isSelected = selectedSlot &&
                                selectedSlot.startTime === slot.startTime &&
                                selectedSlot.endTime === slot.endTime &&
                                selectedSlot.userId === slot.userId;

                              return (
                                <button
                                  key={`${slot.id || idx}-${slot.userId}`}
                                  type="button"
                                  onClick={() => !isBooked && slot.userId && setSelectedSlot({
                                    startTime: slot.startTime,
                                    endTime: slot.endTime,
                                    userId: slot.userId
                                  })}
                                  disabled={isBooked}
                                  className={`p-3 rounded-lg text-sm flex flex-col items-center ${
                                    isBooked
                                      ? 'bg-red-50 text-red-400 cursor-not-allowed'
                                      : isSelected
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <span>{formatTimeRange(slot.startTime, slot.endTime, projects.find(p => p.id === projectId)?.settings?.timezone)}</span>
                                  </div>
                                  {(slotCapacity > 1 || bookedCount > 0) && (
                                    <span className={`text-xs mt-1 px-2 py-0.5 rounded-full ${
                                      isBooked
                                        ? 'bg-red-100 text-red-700'
                                        : bookedCount > 0
                                          ? 'bg-amber-100 text-amber-700'
                                          : isSelected
                                            ? 'bg-white/20 text-white'
                                            : 'bg-gray-200 text-gray-600'
                                    }`}>
                                      {bookedCount}/{slotCapacity}{isBooked ? ' full' : ' booked'}
                                    </span>
                                  )}
                                  {slot.userName && !isBooked && (
                                    <span className="text-xs opacity-75 mt-1">
                                      with {slot.userName}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Selected Appointment Summary */}
                      {selectedSlot && selectedDate && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <Check className="h-4 w-4" />
                            <span className="font-medium">
                              {scheduleType === 'tour' ? 'Tour' : 'Call'} Scheduled
                            </span>
                          </div>
                          <p className="text-sm text-emerald-600 mt-1">
                            {format(new Date(selectedDate), 'EEEE, MMMM d')} at {selectedSlot.startTime}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Notes (collapsible) */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowNotes(!showNotes)}
            className="w-full flex items-center justify-between p-4"
          >
            <span className="font-medium text-gray-700">Notes (optional)</span>
            {showNotes ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showNotes && (
            <div className="px-4 pb-4">
              <textarea
                placeholder="Any additional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              />
            </div>
          )}
        </div>

        {/* Documents Upload (collapsible) */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowDocuments(!showDocuments)}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-emerald-600 mr-2" />
              <h2 className="font-semibold text-gray-900">Documents</h2>
              {(interestFormFile || confirmationFile) && (
                <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  {[interestFormFile, confirmationFile].filter(Boolean).length} attached
                </span>
              )}
            </div>
            {showDocuments ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showDocuments && (
            <div className="px-4 pb-4 space-y-4">
              {/* Interest Form Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interest Form
                </label>
                {interestFormFile ? (
                  <div className="relative bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {interestFormPreview ? (
                          <img
                            src={interestFormPreview}
                            alt="Interest form preview"
                            className="w-12 h-12 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                            <FileText className="h-6 w-6 text-gray-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {interestFormFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(interestFormFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeInterestForm}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                      <Camera className="h-5 w-5" />
                      <span>Take Photo</span>
                      <input
                        ref={interestFormInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleInterestFormSelect}
                        className="hidden"
                      />
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                      <Upload className="h-5 w-5" />
                      <span>Upload</span>
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={handleInterestFormSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Confirmation Text/Screenshot Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmation Text/Screenshot
                </label>
                {confirmationFile ? (
                  <div className="relative bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {confirmationPreview ? (
                          <img
                            src={confirmationPreview}
                            alt="Confirmation preview"
                            className="w-12 h-12 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                            <Image className="h-6 w-6 text-gray-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {confirmationFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(confirmationFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeConfirmation}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                      <Camera className="h-5 w-5" />
                      <span>Take Photo</span>
                      <input
                        ref={confirmationInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleConfirmationSelect}
                        className="hidden"
                      />
                    </label>
                    <label className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                      <Upload className="h-5 w-5" />
                      <span>Upload</span>
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={handleConfirmationSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 text-center">
                Documents are optional. Max file size: 10MB
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom submit button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-inset-bottom">
        {!isValid() && !isSubmitting && missingRequirements().length > 0 && (
          <p className="text-xs text-amber-700 text-center mb-2">
            Still needed: {missingRequirements().join(', ')}
          </p>
        )}
        <button
          onClick={handleSubmit}
          disabled={!isValid() || isSubmitting || uploadingDocs}
          className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
        >
          {isSubmitting || uploadingDocs ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {uploadingDocs ? 'Uploading Documents...' : 'Saving Lead...'}
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5 mr-2" />
              Capture Lead
            </>
          )}
        </button>
      </div>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">Pick Location on Map</h3>
              <button
                onClick={() => setShowMapPicker(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Tap on the map to select the parent's address location. The address will be filled in automatically.
              </p>
              <div
                id="map-picker-container"
                className="h-64 bg-gray-100 rounded-lg overflow-hidden"
                ref={(el) => {
                  if (el && !mapRef.current && typeof google !== 'undefined') {
                    const defaultCenter = gpsLocation || { lat: 40.7128, lng: -74.0060 };
                    mapRef.current = new google.maps.Map(el, {
                      center: defaultCenter,
                      zoom: 15,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                    });

                    // Add click listener
                    mapRef.current.addListener('click', async (e: google.maps.MapMouseEvent) => {
                      if (e.latLng) {
                        const lat = e.latLng.lat();
                        const lng = e.latLng.lng();

                        // Update or create marker
                        if (markerRef.current) {
                          markerRef.current.setPosition(e.latLng);
                        } else {
                          markerRef.current = new google.maps.Marker({
                            position: e.latLng,
                            map: mapRef.current,
                            draggable: true,
                          });

                          // Add drag listener
                          markerRef.current.addListener('dragend', async () => {
                            const pos = markerRef.current?.getPosition();
                            if (pos) {
                              setGpsLocation({ lat: pos.lat(), lng: pos.lng() });
                              await reverseGeocode(pos.lat(), pos.lng());
                            }
                          });
                        }

                        setGpsLocation({ lat, lng });
                        await reverseGeocode(lat, lng);
                      }
                    });

                    // If we have a GPS location, add initial marker
                    if (gpsLocation) {
                      markerRef.current = new google.maps.Marker({
                        position: gpsLocation,
                        map: mapRef.current,
                        draggable: true,
                      });

                      markerRef.current.addListener('dragend', async () => {
                        const pos = markerRef.current?.getPosition();
                        if (pos) {
                          setGpsLocation({ lat: pos.lat(), lng: pos.lng() });
                          await reverseGeocode(pos.lat(), pos.lng());
                        }
                      });
                    }
                  }
                }}
              />
              {gpsLocation && (
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {gpsLocation.lat.toFixed(6)}, {gpsLocation.lng.toFixed(6)}
                </p>
              )}
            </div>
            <div className="flex gap-3 p-4 border-t">
              <button
                onClick={() => setShowMapPicker(false)}
                className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setAddressMode('map');
                  setShowMapPicker(false);
                }}
                disabled={!gpsLocation}
                className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-medium disabled:opacity-50"
              >
                Confirm Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickLeadCapture;
